"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.scrollToTop = scrollToTop;
exports.useIsInView = useIsInView;
exports.useScrollDetect = useScrollDetect;
exports.useScrollPosition = useScrollPosition;
var _react = require("react");
/**
 * useScrollDetect
 *
 * @param {Array} ids Section Ids
 * @param {Number} offset Section Offset Value Top and Bottom
 * @returns {Number} modular scale
 * @private
 */

function useScrollDetect(ids = [], offset = 0) {
  const [activeSection, setActiveSection] = (0, _react.useState)(null);
  const rafRef = (0, _react.useRef)(null);
  const activeSectionRef = (0, _react.useRef)(activeSection);
  const inRange = (0, _react.useMemo)(() => (value, min, max) => value >= min && value <= max, []);
  (0, _react.useEffect)(() => {
    const scrollListener = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = requestAnimationFrame(() => {
        const scrollPosition = window.pageYOffset;
        const calcSectionPos = ids.map(id => {
          const sectionElement = document.getElementById(id);
          if (!sectionElement) return null;
          const rect = sectionElement.getBoundingClientRect();
          const sectionTop = Math.max(0, rect.top + scrollPosition - offset);
          const sectionBottom = Math.max(0, rect.bottom + scrollPosition - offset);
          return {
            id,
            sectionTop,
            sectionBottom
          };
        }).filter(Boolean).find(({
          sectionTop,
          sectionBottom
        }) => inRange(scrollPosition, sectionTop, sectionBottom));
        const newActiveSection = calcSectionPos?.id || '';
        if (newActiveSection !== activeSectionRef.current) {
          activeSectionRef.current = newActiveSection;
          setActiveSection(newActiveSection);
        }
      });
    };
    window.addEventListener('resize', scrollListener);
    window.addEventListener('scroll', scrollListener, {
      passive: true
    });
    scrollListener();
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      window.removeEventListener('resize', scrollListener);
      window.removeEventListener('scroll', scrollListener);
    };
  }, [ids, offset, inRange]);
  return activeSection;
}

/**
 * scrollToTop
 *
 * @returns {null} Smooth scroll to top
 * @private
 */

function scrollToTop() {
  if (typeof window !== 'undefined') {
    window?.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }
}

/**
 * useIsInView
 *
 * @param {Number} ref Reference to the element
 * @returns {Boolean} inView Condition
 * @private
 */

function useIsInView(ref) {
  const [isInView, setIsInView] = (0, _react.useState)(false);
  const threshold = 600;
  (0, _react.useEffect)(() => {
    function handleScroll() {
      const {
        bottom
      } = ref.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const scrollYPos = window.scrollY;
      if (scrollYPos.scrollY > bottom) {
        setIsInView(true);
      }
      return setIsInView(viewportHeight - bottom > threshold);
    }
    window.addEventListener('resize', handleScroll);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.addEventListener('resize', handleScroll);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [ref, isInView]);
  return isInView;
}
function useScrollPosition() {
  const [scrollPosition, setScrollPosition] = (0, _react.useState)(0);
  (0, _react.useEffect)(() => {
    if (typeof window !== 'undefined') {
      const updatePosition = () => {
        setScrollPosition(window.pageYOffset);
      };
      window.addEventListener('scroll', updatePosition);
      return () => window.removeEventListener('scroll', updatePosition);
    }
  }, []);
  return Math.floor(scrollPosition);
}